import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext.tsx";
import api from "../services/api.tsx";
import { useError } from "../contexts/ErrorContext.tsx";
import { useAuth } from "../contexts/AuthContext.tsx";
import cities from "../data/cities.json";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^05\d{9}$/;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, total, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const { setError } = useError();
  const { isAuthenticated } = useAuth();
  const [userInfo, setUserInfo] = useState({
    email: "",
    name: "",
    city: "",
    district: "",
    neighborhood: "",
    address: "",
    phone: ""
  });

  // Giriş yapmışsa kullanıcı bilgilerini çek
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await api.get("/api/user/profile", {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserInfo({
          email: res.data.email,
          name: res.data.fullName || res.data.name,
          address: res.data.address || "",
          phone: res.data.phone || "",
          city: res.data.phone || "",
          district: res.data.district || "",
          neighborhood: res.data.neighborhood || "",
        });
      } catch (err) {
        setError("Kullanıcı bilgileri alınamadı.");
      }
    };
    fetchUserInfo();
  }, [isAuthenticated, setError]);

  // İl değiştiğinde ilçe sıfırlansın
  useEffect(() => {
    setUserInfo((prev) => ({ ...prev, district: "" }));
  }, [userInfo.city]);

  const validateFields = () => {
    if (!userInfo.name || !userInfo.email || !userInfo.city || !userInfo.district || !userInfo.neighborhood || !userInfo.address || !userInfo.phone) {
      setError("Lütfen tüm bilgileri doldurun.");
      return false;
    }
    if (!emailRegex.test(userInfo.email)) {
      setError("Geçerli bir e-posta adresi giriniz.");
      return false;
    }
    if (!phoneRegex.test(userInfo.phone)) {
      setError("Telefon numarası 05XXXXXXXXX formatında olmalıdır.");
      return false;
    }
    return true;
  };

  // Ortalama depozito oranı hesaplama
  let prepayPercentage =
    cart.filter(item => typeof item.depositRate === "number" && !isNaN(item.depositRate)).length > 0
      ? Math.round(
        cart
          .filter(item => typeof item.depositRate === "number" && !isNaN(item.depositRate))
          .reduce((sum, item) => sum + (item.depositRate ?? 0), 0) /
        cart.filter(item => typeof item.depositRate === "number" && !isNaN(item.depositRate)).length
      )
      : 10;
  if (prepayPercentage === 0) prepayPercentage = 10;

  const prepay = (total * 0.1).toFixed(2);

  let prepayTotal = cart.reduce((sum, item) => {
    const percentage = item.depositRate ?? 1;
    return sum + (item.finalPrice * item.quantity * (percentage / 100));
  }, 0);

  if (prepayTotal === 0) {
    prepayTotal = prepay;
  }
  prepayTotal = Number(prepayTotal);

  const handlePayment = async () => {
    setLoading(true);
    try {
      if (cart.length === 0) {
        alert("Sepetinizde ürün yok.");
        return;
      }

      if (!isAuthenticated && !validateFields()) {
        setLoading(false);
        return;
      }

      let token = localStorage.getItem("access_token");

      // 1. Sipariş oluştur
      const orderRequest = {
        products: cart.map(product => ({
          productId: product.productId,
          quantity: product.quantity,
          campaignId: product.campaignId,
          depositRate: product.depositRate > 0 ? product.depositRate : 10,
        })),
        prepayPercentage,
        // Misafir kullanıcı için ek bilgiler
        guestInfo: !isAuthenticated ? userInfo : undefined,
      };

      await api.post("/api/order", orderRequest, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      // // 2. PayTR ödeme token isteği
      // const paytrResponse = await api.post("/api/payment/paytr", {
      //   totalAmount: prepayTotal,
      //   userEmail: userInfo.email,
      //   userName: userInfo.name,
      //   userAddress: userInfo.address,
      //   userPhone: userInfo.phone
      // }, {
      //   headers: token ? { Authorization: `Bearer ${token}` } : undefined
      // });

      // const paymentUrl = paytrResponse.data.paymentUrl;

      // // 3. PayTR ödeme ekranına yönlendir
      // window.location.href = paymentUrl;

      clearCart();
      navigate("/thank-you");
    } catch (err: any) {
      alert("Ödeme sırasında bir hata oluştu.");
      setError(err.response?.data?.Message || "Bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Siparişi Onayla</h2>

      <p className="mb-2">Toplam Tutar: {total.toLocaleString()} TL</p>
      <p className="mb-4">Ödenecek Ön Ödeme (%{prepayPercentage}): {prepayTotal.toFixed(2)} TL</p>

      {/* Misafir kullanıcı ise formu göster */}
      {!isAuthenticated && (
        <div className="mb-4 space-y-2">
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="Ad Soyad"
            value={userInfo.name}
            onChange={e => setUserInfo({ ...userInfo, name: e.target.value })}
          />
          <input
            type="email"
            className="w-full p-2 border rounded"
            placeholder="E-posta"
            value={userInfo.email}
            onChange={e => setUserInfo({ ...userInfo, email: e.target.value })}
          />
          <select
            className="w-full p-2 border rounded"
            value={userInfo.city}
            onChange={e => setUserInfo({ ...userInfo, city: e.target.value })}
          >
            <option value="">İl Seçiniz</option>
            {cities.map(city => (
              <option key={city.name} value={city.name}>{city.name}</option>
            ))}
          </select>
          <select
            className="w-full p-2 border rounded"
            value={userInfo.district}
            onChange={e => setUserInfo({ ...userInfo, district: e.target.value })}
            disabled={!userInfo.city}
          >
            <option value="">İlçe Seçiniz</option>
            {cities.find(c => c.name === userInfo.city)?.districts.map(district => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="Mahalle"
            value={userInfo.neighborhood}
            onChange={e => setUserInfo({ ...userInfo, neighborhood: e.target.value })}
          />
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="Açık Adres"
            value={userInfo.address}
            onChange={e => setUserInfo({ ...userInfo, address: e.target.value })}
          />
          <input
            type="tel"
            className="w-full p-2 border rounded"
            placeholder="Telefon (05XXXXXXXXX)"
            value={userInfo.phone}
            onChange={e => setUserInfo({ ...userInfo, phone: e.target.value })}
          />
        </div>
      )}

      <button
        onClick={handlePayment}
        disabled={loading}
        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded"
      >
        {loading ? "Ödeme Yapılıyor..." : "Ödeme Yap ve Siparişi Tamamla"}
      </button>
    </div>
  );
};

export default CheckoutPage;